import { motion } from 'framer-motion'
import { Room } from '../types'
import type { SendFn } from '../App'
import ItemReactions from './ItemReactions'

interface ResultsProps {
  room: Room
  playerId: string
  send: SendFn
}

export default function Results({ room, playerId, send }: ResultsProps) {
  const rankerName = room.currentRankerId ? room.players[room.currentRankerId]?.name : '???'
  const actual = room.rankerRanking
  const actualLine = room.rankerLineIndex
  const isGroupMode = room.gameMode === 'group'

  const nextRound = () => {
    send('next_round', {})
  }

  const isHost = playerId === room.hostId

  const runningLeaderboard = Object.keys(room.players)
    .filter((pid) => pid !== room.currentRankerId)
    .map((pid) => ({
      playerId: pid,
      name: room.players[pid]?.name || '???',
      total: room.leaderboard[pid] ?? 0,
    }))
    .sort((a, b) => b.total - a.total)

  // Build per-player breakdowns from server-supplied lastRoundScores.
  // In group mode every non-ranker maps to the same RoundScore object so
  // we just pick any one for the headline.
  const breakdownEntries = Object.entries(room.lastRoundScores)
    .map(([pid, score]) => ({
      playerId: pid,
      name: room.players[pid]?.name || '???',
      sideMatches: score.sideMatches,
      exactMatches: score.exactMatches,
      total: score.total,
    }))
    .sort((a, b) => b.total - a.total)

  const groupHeadline = isGroupMode ? breakdownEntries[0] : null

  return (
    <div className="flex-1 flex flex-col px-6 py-6 max-w-lg mx-auto w-full overflow-y-auto">
      <div className="text-center mb-5">
        <p className="font-body text-warmblack/50 text-base mb-1">Round {room.roundNumber}</p>
        <h2 className="font-display text-3xl font-bold text-warmblack mb-1">Results!</h2>
        <p className="font-body text-lg text-warmblack/60">How well did you read {rankerName}?</p>
      </div>

      {/* Ranker's actual ranking */}
      <div className="mb-5">
        <h3 className="font-display text-base font-bold text-warmblack/70 mb-2">
          {rankerName}'s ranking:
        </h3>
        {/* gap-3 leaves room for the half-overhanging reaction badges on
            the bottom-right of each item; tighter and they overlap. */}
        <div className="flex flex-col gap-3">
          {actual.map((item, i) => (
            <div key={item}>
              {i === actualLine && (
                <div className="flex items-center gap-2 py-0.5">
                  <div className="flex-1 h-0.5 bg-coral/40 rounded-full" />
                  <span className="text-xs font-display font-bold text-coral">THE LINE</span>
                  <div className="flex-1 h-0.5 bg-coral/40 rounded-full" />
                </div>
              )}
              <ItemReactions itemText={item} room={room} playerId={playerId} send={send}>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-base font-body ${
                  i < actualLine ? 'bg-sage/15 text-warmblack' : 'bg-coral/10 text-warmblack'
                }`}>
                  <span className="font-bold w-7 text-center text-warmblack/40 text-sm">#{i + 1}</span>
                  <span className="font-bold">{item}</span>
                </div>
              </ItemReactions>
            </div>
          ))}
          {actualLine === actual.length && (
            <div className="flex items-center gap-2 py-0.5">
              <div className="flex-1 h-0.5 bg-coral/40 rounded-full" />
              <span className="text-xs font-display font-bold text-coral">THE LINE</span>
              <div className="flex-1 h-0.5 bg-coral/40 rounded-full" />
            </div>
          )}
        </div>
      </div>

      {/* Group headline (group mode only) */}
      {isGroupMode && groupHeadline && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-5 bg-white rounded-xl p-4 border border-warmblack/10 shadow-sm text-center"
        >
          <p className="font-body text-sm text-warmblack/60 mb-1">The group scored</p>
          <p className="font-display text-4xl font-bold text-coral">{groupHeadline.total} pts</p>
          <p className="font-body text-sm text-warmblack/50 mt-1">
            {groupHeadline.exactMatches} exact · {groupHeadline.sideMatches} same-side
          </p>
        </motion.div>
      )}

      {/* Per-player breakdown (individual mode only) */}
      {!isGroupMode && breakdownEntries.length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-5"
        >
          <h3 className="font-display text-base font-bold text-warmblack/70 mb-2">This Round</h3>
          <div className="flex flex-col gap-1.5">
            {breakdownEntries.map((p, i) => (
              <motion.div
                key={p.playerId}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
                  i === 0 && p.total > 0
                    ? 'bg-sunflower/20 border-sunflower/40'
                    : 'bg-white border-warmblack/10'
                }`}
              >
                <span className="font-body text-lg font-bold text-warmblack">{p.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-body text-warmblack/50">
                    {p.exactMatches} exact · {p.sideMatches} side
                  </span>
                  <span className="font-display text-xl font-bold text-coral">{p.total} pts</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Cumulative leaderboard */}
      {room.roundNumber > 1 && runningLeaderboard.length > 0 && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mb-6 bg-lavender/20 rounded-xl p-4 border border-lavender/30"
        >
          <h3 className="font-display text-base font-bold text-warmblack/70 mb-2">Leaderboard</h3>
          <div className="flex flex-col gap-1">
            {runningLeaderboard.map((player, i) => (
              <div key={player.playerId} className="flex items-center justify-between px-3 py-2">
                <span className="font-body text-base font-bold text-warmblack">
                  {i === 0 ? '1st' : i === 1 ? '2nd' : i === 2 ? '3rd' : `${i + 1}th`} {player.name}
                </span>
                <span className="font-display text-lg font-bold text-warmblack/70">{player.total} pts</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Next round — only host can trigger */}
      {isHost && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.8 }}
          whileTap={{ scale: 0.95 }}
          onClick={nextRound}
          className="w-full bg-sunflower hover:bg-sunflower/80 text-warmblack font-display font-bold text-2xl py-5 rounded-2xl shadow-lg transition-all"
        >
          {room.bowl.length < 5 ? 'Back to Lobby (need more items!)' : 'Next Round →'}
        </motion.button>
      )}
      {!isHost && (
        <p className="text-center text-warmblack/40 font-body text-base">
          Waiting for the host to continue…
        </p>
      )}
    </div>
  )
}
